/**
 * 火山引擎 DataFinder 埋点 SDK（私有化部署版）
 *
 * 接入方式：全局收集器（TeaAnalyticsObject / collectEvent）与 SDK 脚本由各国家 index.html 提前注入，
 * 本方法仅负责调用 init / config / start（SDK 加载前会入队列，加载后自动 flush）。
 * 使用方式：
 *   import { initVolcAnalytics, trackEvent, setVolcUserId } from '@/utils/volcAnalytics'
 *   initVolcAnalytics({ app_id: 10000025, channel_domain: 'https://snssdk.jtexpress.my' })
 *   trackEvent('button_click', { page: 'home', button: 'submit' })
 *   setVolcUserId('user_123')
 */

export interface VolcInitOptions {
  /** 应用 ID */
  app_id: number
  /** 私有化部署服务地址（SDK 上报域名） */
  channel_domain: string
  /** 禁止 SDK 自身监控事件 onload 的上报，默认 true */
  disable_sdk_monitor?: boolean
  /** 全局变量名，默认 collectEvent（需与 index.html 中注入的一致） */
  export_name?: string
}

let initialized = false

/**
 * 初始化火山埋点 SDK
 * 前置条件：全局收集器（collectEvent）与 SDK 脚本已在 index.html 中注入。
 * 本方法仅执行 init / config / start（SDK 加载前会入队列，加载后自动 flush）。
 */
export function initVolcAnalytics(options: VolcInitOptions): void {
  if (initialized || typeof window === 'undefined') return

  const { app_id, channel_domain, disable_sdk_monitor = true, export_name = 'collectEvent' } = options

  const win = window as any
  const collect = win[export_name]
  if (typeof collect !== 'function') {
    console.error('[VolcAnalytics] 全局收集器 collectEvent 未注入，请检查 index.html 配置')
    return
  }

  try {
    collect('init', {
      app_id,
      channel_domain,
      disable_sdk_monitor,
      disable_auto_pv: false,
      disable_heartbeat: true,
      disable_route_report: false,
      disable_session: true,
      log: true // 调试：临时打开 SDK 日志
    })
    collect('config', {
      platform: 'Yoyi-Site'
    })
    collect('start')
    initialized = true
  } catch (e) {
    console.error('[VolcAnalytics] init 调用失败:', e)
  }
}

/**
 * 上报自定义事件
 * @param eventName 事件名
 * @param params 事件参数（可选）
 */
export function trackEvent(eventName: string, params?: Record<string, any>): void {
  try {
    const collect = (window as any)['collectEvent']
    if (typeof collect === 'function') {
      collect(eventName, params || {})
    }
  } catch (e) {
    // 静默失败，不影响业务
  }
}

/**
 * 设置用户 ID（登录后调用，关联用户身份）
 * @param userId 用户唯一标识
 */
export function setVolcUserId(userId: string | number | undefined | null): void {
  if (!userId) return
  try {
    const collect = (window as any)['collectEvent']
    if (typeof collect === 'function') {
      collect('config', {
        user_unique_id: String(userId),
      })
    }
  } catch (e) {
    console.error('[VolcAnalytics] 设置用户 ID 失败:', e)
  }
}

/**
 * 设置全局公共属性（所有事件都会自动带上）
 * @param properties 公共属性对象
 */
export function setVolcCommonProperties(properties: Record<string, any>): void {
  try {
    const collect = (window as any)['collectEvent']
    if (typeof collect === 'function') {
      collect('config', {
        header: properties,
      })
    }
  } catch (e) {
    // 静默失败
  }
}

export default initVolcAnalytics
